import React, { useEffect, useState } from "react";
import { Button, Modal, Pagination } from "react-bootstrap";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import AddRecommendationRule from "./addRecommendationRule";
import AdminDashboard from "./admindashboard";
import "./hm-theme.css";

const BmiRecommendationRules = () => {
  const { companyId } = useParams();
  const [loader, setLoader] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const navigate = useNavigate();

  // Test linking states
  const [isLinkTestModalOpen, setIsLinkTestModalOpen] = useState(false);
  const [selectedRecommendationRule, setSelectedRecommendationRule] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const testsPerPage = 10;
  const [labTests, setLabTests] = useState([]);
  const [mappedTestIds, setMappedTestIds] = useState([]);
  const [initialMappedTestIds, setInitialMappedTestIds] = useState([]);
  const [isViewTestsModalOpen, setIsViewTestsModalOpen] = useState(false);
  const [viewedTestsList, setViewedTestsList] = useState([]);
  const [viewedRule, setViewedRule] = useState(null);

  const handleOpenViewTestsModal = async (item) => {
    setLoader(true);
    try {
      if (labTests.length === 0) {
        await fetchLabTests();
      }
      const token = JSON.parse(localStorage.getItem("data")).token;
      const response = await axios.get(
        `/api/recommendationruletests/ruleid=${item.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setViewedTestsList(response.data);
      setViewedRule(item);
      setIsViewTestsModalOpen(true);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handleToggleTestIsActive = async (mapping) => {
    setLoader(true);
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const payload = {
        ...mapping,
        isActive: !mapping.isActive,
      };
      await axios.post(`/api/recommendationruletests/update`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        `Test ${payload.isActive ? "enabled" : "disabled"} successfully!`
      );
      setViewedTestsList((prev) =>
        prev.map((m) =>
          m.ruleId === mapping.ruleId && m.testId === mapping.testId
            ? { ...m, isActive: !mapping.isActive }
            : m
        )
      );
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async () => {
    setLoader(true);
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const response = await axios.get(
        `/api/recommendationrules/companyid=${companyId}/rulename=BMI`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setConditions(response.data);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const fetchLabTests = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const response = await axios.get(`/api/labtest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabTests(response.data);
    } catch (error) {
      console.error("Error fetching lab tests:", error);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  useEffect(() => {
    if (companyId) {
      getData();
    }
  }, [companyId]);

  const handleAddClose = () => {
    setIsAddModalOpen(false);
    getData();
  };

  const handleToggleIsActive = async (condition) => {
    setLoader(true);
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const payload = {
        ...condition,
        isActive: !condition.isActive,
      };
      await axios.post(`/api/recommendationrules/update`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        `Condition ${payload.isActive ? "enabled" : "disabled"} successfully!`
      );
      getData();
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handleOpenLinkTestModal = async (condition) => {
    setSelectedRecommendationRule(condition);
    setMappedTestIds([]);
    setCurrentPage(1); // Reset page on open
    setIsLinkTestModalOpen(true);
    setLoader(true);
    try {
      if (labTests.length === 0) {
        await fetchLabTests();
      }
      const token = JSON.parse(localStorage.getItem("data")).token;
      const response = await axios.get(
        `/api/recommendationruletests/ruleid=${condition.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const testIds = response.data.map((mapping) => mapping.testId);
      setMappedTestIds(testIds);
      setInitialMappedTestIds(testIds);
      setLoader(false);
      setIsLinkTestModalOpen(true);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handleTestCheckboxChange = (testId) => {
    setMappedTestIds((prev) => {
      if (prev.includes(testId)) {
        return prev.filter((id) => id !== testId);
      } else {
        return [...prev, testId];
      }
    });
  };

  const saveTestMapping = async () => {
    setLoader(true);
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;

      // 1. First we need to delete existing mappings for this BMI condition to update cleanly
      // Let's get existing mapping first
      const existingRes = await axios.get(
        `/api/recommendationruletests/ruleid=${selectedRecommendationRule.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const existingMappings = existingRes.data;

      // Delete existing
      for (let mapping of existingMappings) {
        await axios.delete(`/api/recommendationruletests`, {
          headers: { Authorization: `Bearer ${token}` },
          data: mapping,
        });
      }

      // 2. Create new mappings
      if (mappedTestIds.length > 0) {
        const payload = mappedTestIds.map((testId) => ({
          ruleId: selectedRecommendationRule.id,
          testId: testId,
        }));
        await axios.post(`/api/recommendationruletests/create`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      toast.success("Test mapping saved successfully!");
      setIsLinkTestModalOpen(false);
      getData();
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  return (
    <div>
      <AdminDashboard />
      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">
          {loader && <div className="loading"></div>}

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">monitor_weight</i>
                BMI Based Recommendation Rules
              </h2>
              <p className="hm-page-subtitle">Define BMI-based conditions for test recommendations</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary rounded-pill px-3"
                onClick={() => navigate(-1)}
              >
                <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px' }}>arrow_back</i>
                Back
              </button>
              <button
                className="btn btn-primary rounded-pill px-4"
                onClick={() => setIsAddModalOpen(true)}
              >
                Add Condition
              </button>
            </div>
          </div>

          {/* Rules Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">manage_search</i>
                BMI Conditions
              </h3>
              <span className="hm-badge hm-badge-primary">{conditions.length} conditions</span>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Operator</th>
                      <th>BMI Value</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conditions.length === 0 ? (
                      <tr>
                        <td colSpan="5">
                          <div className="hm-empty-state">
                            <i className="material-icons">rule_folder</i>
                            <p>No BMI conditions added yet. Click "Add Condition" to get started.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      conditions.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td><span className="hm-badge hm-badge-primary">{item.operator}</span></td>
                          <td><strong>{item.conditionValue}</strong></td>
                          <td>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`statusToggle-${item.id}`}
                                checked={!!item.isActive}
                                onChange={() => handleToggleIsActive(item)}
                                style={{ cursor: "pointer" }}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                onClick={() => handleOpenLinkTestModal(item)}
                              >
                                Link Test
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                                onClick={() => handleOpenViewTestsModal(item)}
                              >
                                View Tests
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {isAddModalOpen && (
          <AddRecommendationRule
            isOpen={isAddModalOpen}
            onRequestClose={handleAddClose}
            companyId={companyId}
          />
        )}

        <Modal
          show={isLinkTestModalOpen}
          onHide={() => setIsLinkTestModalOpen(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Link Tests to Recommendation Rule: {selectedRecommendationRule?.condition}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {(() => {
              const availableLabTests = labTests.filter(test => !initialMappedTestIds.includes(test.id));
              return availableLabTests.length > 0 ? (
                <>
                  <table className="table table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Select</th>
                      <th>Test Name</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableLabTests.slice((currentPage - 1) * testsPerPage, currentPage * testsPerPage).map((test) => (
                      <tr key={test.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={mappedTestIds.includes(test.id)}
                            onChange={() => handleTestCheckboxChange(test.id)}
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                        <td>{test.testname}</td>
                        <td>{test.testCost?.cost ? `₹${test.testCost.cost}` : "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="d-flex justify-content-end mt-3">
                  <Pagination size="sm">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                    />
                    {[...Array(Math.ceil(availableLabTests.length / testsPerPage)).keys()].map((num) => {
                      const page = num + 1;
                      // Display a subset of pages if there are many, but since it's just tests, maybe simple rendering is fine.
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      disabled={currentPage === Math.ceil(availableLabTests.length / testsPerPage)}
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                    />
                  </Pagination>
                </div>
              </>
            ) : (
              <p>No available lab tests to link.</p>
            );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setIsLinkTestModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={saveTestMapping}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={isViewTestsModalOpen} onHide={() => setIsViewTestsModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Linked Tests</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {viewedTestsList.length > 0 ? (
              <table className="table table-sm table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Test Name</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {viewedTestsList.map((mapping) => {
                    const test = labTests.find((t) => t.id === mapping.testId);
                    return (
                      <tr key={mapping.testId}>
                        <td>{test?.testname || "Unknown"}</td>
                        <td>{test?.testCost?.cost ? `₹${test.testCost.cost}` : "N/A"}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`viewTestToggle-${mapping.testId}`}
                              checked={!!mapping.isActive}
                              onChange={() => handleToggleTestIsActive(mapping)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p>No linked tests.</p>
            )}
          </Modal.Body>
        </Modal>

        </div>
      </div>
    </div>
  );
};

export default BmiRecommendationRules;
